import { FirebaseService } from "@shared/lib/services/firebase.service";
import { ToastService } from "@shared/lib/services/toast.service";
import { Alert, Button } from "@shared/ui";
import { FileSpreadsheet } from "lucide-react";
import { useRef, useState } from "react";
import modalStyles from "../styles.module.scss";
import styles from "../upload.module.scss";
import { CreateDeliverableFormButton } from "./CreateDeliverableFormButton";

interface UploadStepProps {
  briefId: string;
  templateSheetId?: string;
  isSyncing: boolean;
  isParsing: boolean;
  isDownloadingXlsx: boolean;
  hasNoDeliverables?: boolean;
  onDeliverableCreated?: () => void;
  description?: string;
  storageFolder?: string;
  onDownloadTemplate: () => void;
  onDownloadXlsx: () => void;
  onCsvUploaded: (csvUrl: string) => void;
}

export function UploadStep({
  briefId,
  templateSheetId,
  isSyncing,
  isParsing,
  isDownloadingXlsx,
  hasNoDeliverables = false,
  onDeliverableCreated,
  description = "Upload a CSV with creator handles, platforms, commercials, and deliverables. After processing you’ll review ready rows and any failed creators before they are added to the brief.",
  storageFolder,
  onDownloadTemplate,
  onDownloadXlsx,
  onCsvUploaded,
}: UploadStepProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isUploading = uploading || isParsing;

  const handleFileSelect = async (selectedFile: File) => {
    const extension = selectedFile.name
      .toLowerCase()
      .substring(selectedFile.name.lastIndexOf("."));
    if (![".csv"].includes(extension)) {
      ToastService.error("Please upload a CSV file");
      return;
    }

    setFile(selectedFile);
    try {
      setUploading(true);
      const csvUrl = await FirebaseService.uploadFileToFirebase(
        storageFolder || `campaign-briefs/${briefId}/bulk-creators`,
        selectedFile
      );
      onCsvUploaded(csvUrl);
    } catch {
      ToastService.error("File upload failed. Please try again.");
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.content}>
      <p className={styles.description}>{description}</p>
      {hasNoDeliverables ? (
        <Alert
          type="warning"
          showIcon
          className={styles.deliverablesAlert}
          message={
            <div className={styles.deliverablesAlertContent}>
              <div>
                <p className={styles.deliverablesAlertTitle}>
                  No deliverables created
                </p>
                <p className={styles.deliverablesAlertDescription}>
                  Create deliverables if you need them in the template.
                </p>
              </div>
              <CreateDeliverableFormButton
                briefId={briefId}
                onCreated={onDeliverableCreated}
              />
            </div>
          }
        />
      ) : null}
      <div className={styles.templateCard}>
        <p className={styles.templateLabel}>Download template</p>
        <div className={styles.templateActions}>
          <button
            type="button"
            className={styles.templateLink}
            onClick={onDownloadXlsx}
            disabled={isDownloadingXlsx}
          >
            {isDownloadingXlsx ? "Downloading Excel…" : "Download as Excel"}
          </button>
          <button
            type="button"
            className={styles.templateLink}
            onClick={onDownloadTemplate}
            disabled={isSyncing || !templateSheetId}
          >
            {isSyncing ? "Preparing Google Sheet…" : "Download Google Sheet"}
          </button>
        </div>
      </div>

      <div
        className={`${styles.dropZone} ${
          isDragging || isUploading ? styles.dropZoneActive : ""
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const dropped = e.dataTransfer.files[0];
          if (dropped && !isUploading) handleFileSelect(dropped);
        }}
      >
        <div className={styles.dropZoneIcon}>
          <FileSpreadsheet size={32} />
        </div>
        <h4 className={styles.dropZoneTitle}>
          {isUploading ? "Uploading sheet…" : "Drag and drop your file here"}
        </h4>
        <p className={styles.dropZoneSubtitle}>
          {isParsing
            ? "Parsing uploaded sheet"
            : uploading
              ? "Uploading to storage"
              : "Supports .csv"}
        </p>
        <label className={styles.browseButton}>
          Browse Files
          <input
            type="file"
            ref={fileInputRef}
            className={styles.hiddenInput}
            accept=".csv"
            disabled={isUploading}
            onChange={(e) => {
              const selected = e.target.files?.[0];
              if (selected) handleFileSelect(selected);
            }}
          />
        </label>
      </div>

      {file && (
        <div className={styles.fileInfo}>
          <FileSpreadsheet size={16} />
          <span className={styles.fileName}>{file.name}</span>
          {isUploading && (
            <span className={styles.uploadingText}>
              {uploading ? "Uploading..." : "Parsing..."}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface UploadFooterProps {
  onClose: () => void;
}

export function UploadFooter({ onClose }: UploadFooterProps) {
  return (
    <div className={modalStyles.modalFooter}>
      <Button
        onClick={onClose}
        type="text"
        className={modalStyles.cancelButton}
      >
        Cancel
      </Button>
    </div>
  );
}
